import { Component, OnInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Place } from '@app/modules/model/place';
import { Ticket } from '@app/modules/model/ticket';
import { TicketOffer } from '@app/modules/model/ticketoffer';
import { TicketProduct } from '@app/modules/model/ticketproduct';
import { TicketOption } from '@app/modules/model/ticketoption';
import { Product } from '@app/modules/model/product';
import { Offer } from '@app/modules/model/offer';

import { languageSupport, languageService } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

interface OfferProductData {
    requested: Array<TicketProduct>;
    product: Product;
    market: number;
    weight: number;
    charge: number;
    options: Array <TicketOption>;
};

class OfferProduct {

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/    

    constructor(private lang: languageService, private data: OfferProductData, private offer: TicketOffer){
        // nothing to do
    }

    get requested(){
        return this.data.requested;
    }

    get product(){
        return this.data.product;
    }

    get market(){
        return this.data.market;
    }

    get weight(){
        return this.data.weight;
    }

    get charge(){
        return this.data.charge;
    }

    get options(){
        return this.data.options;
    }

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    private get TotalKg(){
        if (this.product.IsWeighted){
            return (this.requested[0].weight / 1000);
        }
        return null;
    }

    get Amount(){
        let _amount = this.requested.length; 
        if (this.product.IsWeighted){
            return (Math.round(_amount * this.TotalKg * 100) / 100).toFixed(2) + this.lang.tr("@kilograms");
        }
        return _amount.toString();        
    }

    get Requested(){    // if we do not display the innerHTML label will not refresh on safari
        return this.Amount + ' x <b>' + this.product.name + '</b>' 
    }

    get ProductWeight(){
        let _totalkg = this.TotalKg;
        if (_totalkg){
            return _totalkg.toFixed(2) + this.lang.tr("@kilograms");
        }
        return null;
    }

    get ProductPrice(){
        let _price = (this.product.MarketPrice) ? this.requested[0].market : this.product.price;        
        if (this.product.IsWeighted){
            return _price.toFixed(2) + this.lang.tr('@currency') + '/' + this.lang.tr('@kilograms') ;
        }
        return _price.toFixed(2) + this.lang.tr('@currency');
    }

    get TotalPrice(){
        let _price = 0;
        for (let _ticketproduct of this.requested){
            _price += _ticketproduct.TotalPrice;
        }
        return Math.round(_price * 100)/100;    
    }

    get ChargePrice(){
        let _ofprice = this.offer.offer.price;
        if (this.offer.fixed){
            _ofprice = this.offer.charge;
        }

        let _price = 0;
        for (let _ticketproduct of this.requested){
            _price += _ticketproduct.OfferCharge(Number(_ofprice));
        }
        return _price;
    }

    get OfferSavings(){
        let _totalprice = this.TotalPrice;
        let _offerPrice = this.ChargePrice;

        return {
            amount: Math.round((_totalprice - _offerPrice)*100) / 100,
            percent: Math.round((1 - (_offerPrice / _totalprice)) * 100)
        }
    }
}

@Component({
    selector: 'ui-offer-view',
    templateUrl:'./ui-view.html',
    styleUrls: [ 
        './ui-view.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiOfferView extends languageSupport implements OnInit, OnDestroy {
    @Input('place') _place: Place = null;
    @Input('ticket') _ticket: Ticket = null;
    @Input('ticketoffer') _ticketoffer: TicketOffer = null;
    @Input('amount') _itemstocart: number = 1;

    private _user_subscription = null;

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    get Offer(): Offer {
        if (this._ticketoffer){
            return this._ticketoffer.offer;
        }
        return null;
    }

    private get user(){
        // Usuario EFECTIVO (camarero del PIN / principal bloqueado), no data.user:
        // si no, un camarero sin permiso de invitar heredaría el del owner. Ver
        // product/ui-view (mismo criterio).
        return this.data.EffectiveUser;
    }

    get CanCompliment(){
        return this._place.AllowCompliment(this.user);
    }

    private _findOfferProduct(needle: TicketProduct, haystack: Array <OfferProduct>){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            let _same = true;

            let _tp1 = needle;
            let _tp2 = haystack[_idx];

            if (_same && (_tp1.product != _tp2.product)){
                _same = false;  // not same product
            }

            if (_same && (_tp1.charge != _tp2.charge)){
                _same = false;  // not same price
            }

            if (_same && (_tp1.market !=  _tp2.market)){
                _same = false;  // not same markat price
            }
            
            if (_same && (_tp1.weight !=  _tp2.weight)){
                _same = false;  // not same weight
            }

            if (_same && (_tp1.options.length != _tp2.options.length)){
                _same = false;   // not same number of options
            }
        
            if (_same){
                for (let _p1option of _tp1.options){
                    _same = _tp2.options.some(
                    _p2option => {
                        return (_p2option.option == _p1option.option);
                    });
                }    
            }

            if (_same){
                for (let _p2option of _tp2.options){
                    _same = _tp1.options.some(
                    _p1option => {
                        return (_p1option.option == _p2option.option);
                    });
                }    
            }        

            if (_same){
                return _idx;
            }
        }

        return -1;  // not found
    }

    private _addOfferProduct(_product: TicketProduct, _offerproducts: Array<OfferProduct>){
        let _idx = this._findOfferProduct(_product, _offerproducts);
        if (_idx != -1){
            _offerproducts[_idx].requested.push(_product);
        }
        else {  // product not added to the offer
            let _productdata: OfferProductData = {
                requested: [ _product ],
                product: _product.product,
                market: _product.market,
                weight: _product.weight,
                charge: _product.charge,
                options: _product.options
            };

            _offerproducts.push(new OfferProduct(this.lang, _productdata, this._candidate));
        }
    }

    private _offerproducts = null;
    get Products(){
        if (!this._ticketoffer){
            return [];
        }

        if (!this._offerproducts){
            let _products: Array <OfferProduct> = [];

            let _ticket = this._ticketoffer.ticket;
            for (let _ticketproduct of _ticket.products){
                if (_ticketproduct.offer && _ticketproduct.IsValid && (_ticketproduct.offer == this._ticketoffer)){
                    this._addOfferProduct(_ticketproduct, _products);
                }
            }

            this._offerproducts = _products;            
        }

        return this._offerproducts;
    }

    get Price(){
        let _price = Number(this.RepriceForm.get('price').value);
        if (_price !== null){
            let _parts = (Math.abs(_price)).toString().split('.');

            let _int = _parts[0];
            let _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : "00";
            let _str = Number(_price).toFixed(2);
            let _neg = (_price < 0); 

            return {
                Int: _int,
                Dec: _dec,
                Str: _str,
                Neg: _neg
            }    
        }

        return null;        
    }

    /***********************************/
    /* OFFER REPRICE                   */
    /***********************************/

    private _repriceform = null;
    get RepriceForm(){
        if (!this._ticketoffer){
            return null;
        }

        if (this._repriceform == null) {
            this.LoadRepriceForm();
        }
        return this._repriceform;
    }

    private LoadRepriceForm(){
        if (!this._ticketoffer){
            return;
        }     

        let _reprice = this._ticketoffer.fixed;  
        let _price = this._ticketoffer.price || this._ticketoffer.offer.price;
        let _invite = (this._ticketoffer.charge == 0);

        if (this._repriceform == null) {
            this._repriceform = new FormGroup({
                reprice: new FormControl(_reprice, [
                    // no validators
                ]),
                price: new FormControl(_price, [
                    Validators.required
                ]),
                invite: new FormControl(_invite, [
                    // no validators
                ])
            });   
        }
        else {
            this._repriceform.patchValue({
                reprice: _reprice,
                price: _price,
                invite: _invite
            });

            this._repriceform.markAsPristine();
        }

        this.OnOfferInvite();
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _candidate: TicketOffer = null;
    ngOnInit(){
        this._candidate = this._ticketoffer.Copy();

        this._user_subscription = this.user.OnRefresh.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/
    
    @Output('onPanel') _onPanel = new EventEmitter<any>();
    @Output('onClose') _onClose = new EventEmitter<any>();

    OnClose(){
        this._onPanel.emit({
            scnd: null,
            args: null
        })

        this._onClose.emit();
    }

    OnCancel(){
        this.OnClose();
    }

    OnUpdate(){
        // El reprice/invitación solo se aplica con permiso de invitar del usuario
        // EFECTIVO (ver product/ui-view): sin permiso, fixed=false (no invita).
        this._ticketoffer.fixed = !!this.RepriceForm.get('reprice').value && this.CanCompliment;
        this._ticketoffer.price = Number(this.RepriceForm.get('price').value);

        this._ticket.DoRefresh('OFFER', true);
        this.OnClose();
    }

    /* form change events */

    private _ApplyToCandidate(){
        this._candidate.fixed = this.RepriceForm.get('reprice').value;
        this._candidate.price = Number(this.RepriceForm.get('price').value);

        this.change.markForCheck();
    }

    OnOfferReprice(){
        if (!this.RepriceForm.get('price').value){
            this.RepriceForm.get('charge').setValue(this._ticketoffer.charge);
        }

        this._ApplyToCandidate();
    }

    OnPriceChange(){
        let _formcontrol = this.RepriceForm.get('price');

        let _value = _formcontrol.value;
        if (_value){
            _formcontrol.setValue(parseFloat(_value.toString().replace(",", ".")).toFixed(2));            
        }
        else {
            _formcontrol.setValue(null);
        }

        this._ApplyToCandidate();
    }

    OnOfferInvite(){
        let _invite = this.RepriceForm.get('invite').value;
        if (_invite){
            this.RepriceForm.get('price').setValue(0);
            this.RepriceForm.get('price').disable();
        }
        else {
            this.RepriceForm.get('price').setValue(this._ticketoffer.offer.price);
            this.RepriceForm.get('price').enable();
        }

        this._ApplyToCandidate();
    }
}
